<template>
  <div v-if="showAccount" class="tw-flex tw-w-full tw-flex-col tw-gap-4">
    <!-- Warning banner for missing calendar access -->
    <div
      v-if="!hasCalendarAccess"
      class="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-amber-50 tw-border tw-border-amber-200 tw-p-3"
    >
      <v-icon color="warning" small>mdi-alert</v-icon>
      <div class="tw-flex-1 tw-text-sm tw-text-amber-800">
        Calendar access not granted. Your availability won't be imported.
      </div>
      <v-btn
        x-small
        color="warning"
        class="tw-normal-case"
        @click="reauthenticateCalendarAccount"
      >
        Reconnect
      </v-btn>
    </div>

    <!-- Calendar Account Header -->
    <div class="tw-flex tw-items-center tw-justify-between">
      <div class="tw-flex tw-w-full tw-flex-row tw-items-center tw-gap-2">
        <div v-if="toggleState" class="tw-flex tw-items-center">
          <v-checkbox
            v-model="account.enabled"
            @change="(enabled) => toggleCalendarAccount(enabled)"
            hide-details
          />
          <div
            class="-tw-ml-2 tw-h-fit tw-w-fit tw-cursor-pointer"
            @click="
              () => {
                showSubCalendars = !showSubCalendars
              }
            "
          >
            <v-icon
              :class="`tw-rotate-${showSubCalendars ? 90 : 0}`"
              class="tw-text-dark-gray tw-transition-all"
              >mdi-chevron-right</v-icon
            >
          </div>
        </div>
        <UserAvatarContent v-else :size="24" :user="account" />
        <div
          :class="toggleState && !fillSpace ? 'tw-w-[180px]' : ''"
          class="tw-align-text-middle tw-text-gray-900 tw-inline-block tw-break-words tw-text-sm tw-font-medium"
        >
          {{ account.email }}
        </div>
        <v-tooltip top v-if="accountHasError">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="reauthenticateCalendarAccount"
            >
              <v-icon>mdi-alert-circle</v-icon>
            </v-btn>
          </template>
          <span>{{ reauthenticateBtnText }}</span>
        </v-tooltip>
      </div>

      <!-- Provider Icon -->
      <v-icon
        v-if="account.calendarType === 'google'"
        color="#4285F4"
        class="tw-ml-2"
      >
        mdi-google
      </v-icon>
      <v-icon
        v-else-if="account.calendarType === 'outlook'"
        color="#0078D4"
        class="tw-ml-2"
      >
        mdi-microsoft-outlook
      </v-icon>
      <v-icon
        v-else-if="account.calendarType === 'apple'"
        color="#000000"
        class="tw-ml-2"
      >
        mdi-apple
      </v-icon>

      <!-- Delete account button (only show in old layout) -->
      <v-btn
        v-if="toggleState"
        icon
        :class="`tw-opacity-${
          account.email == selectedRemoveEmail && removeDialog ? '100' : '0'
        } ${!allowDelete ? 'tw-hidden' : ''}`"
        class="group-hover:tw-opacity-100"
        @click="openRemoveDialog"
        ><v-icon color="#4F4F4F">mdi-close</v-icon></v-btn
      >
    </div>

    <!-- New Controls (only show when not in toggleState) -->
    <div v-if="!toggleState" class="tw-flex tw-flex-col tw-gap-4">
      <!-- Manual Disconnect Button -->
      <v-btn
        outlined
        class="tw-normal-case tw-text-red"
        @click="openRemoveDialog"
        block
      >
        <span class="tw-text-center">
          <div>Revoke Calendar Access</div>
        </span>
      </v-btn>

      <!-- Automatic Disconnect Toggle -->
      <div class="tw-flex tw-flex-col tw-gap-2">
        <div class="tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-gray-700 tw-text-sm tw-font-medium">
            Automatically Disconnect
          </span>
          <v-switch
            :input-value="autoDisconnect"
            @change="handleAutoDisconnectChange"
            color="primary"
            hide-details
            class="tw-mt-0"
          />
        </div>

        <!-- Helper text -->
        <div class="tw-text-gray-500 tw-text-xs">
          {{
            autoDisconnect
              ? "(requires manual reconnection for future events)"
              : "(automatic calendar share for future events)"
          }}
        </div>
      </div>
    </div>

    <!-- Sub-calendar accounts -->
    <v-expand-transition>
      <div v-if="showSubCalendars" class="tw-space-y-2 tw-bg-[#EBF7EF] tw-py-2">
        <div
          v-for="(subCalendar, id) in account.subCalendars"
          :key="id"
          class="tw-flex tw-flex-row tw-items-start"
        >
          <v-checkbox
            v-model="subCalendar.enabled"
            @change="(enabled) => toggleSubCalendarAccount(enabled, id)"
            class="-tw-mt-px"
            hide-details
          />
          <div
            :class="!fillSpace ? 'tw-w-40' : ''"
            class="tw-align-text-middle tw-ml-8 tw-inline-block tw-break-words tw-text-sm"
          >
            {{ subCalendar.name }}
          </div>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex"
import { authTypes, calendarTypes } from "@/constants"
import {
  post,
  _delete,
  signInGoogle,
  getCalendarAccountKey,
  patch, // Make sure patch is imported
} from "@/utils"
import UserAvatarContent from "@/components/UserAvatarContent.vue"

export default {
  name: "CalendarAccount",

  props: {
    toggleState: { type: Boolean, default: false },
    account: { type: Object, default: () => {} },
    eventId: { type: String, default: "" },
    calendarEventsMap: { type: Object, default: () => {} },
    removeDialog: { type: Boolean, default: false },
    selectedRemoveEmail: { type: String, default: "" },
    syncWithBackend: { type: Boolean, default: true },
    fillSpace: { type: Boolean, default: false },
  },

  components: {
    UserAvatarContent,
  },

  data: () => ({
    showSubCalendars: false,
    autoDisconnect: true, // Changed default to true
  }),

  computed: {
    ...mapState(["authUser"]),
    allowDelete() {
      return !(
        (this.account.calendarType == calendarTypes.GOOGLE &&
          this.account.email == this.authUser.email) ||
        this.toggleState
      )
    },
    hasCalendarAccess() {
      // Check if the user granted calendar permissions during OAuth
      // If hasCalendarAccess is undefined (old accounts), assume true for backward compatibility
      return this.account.hasCalendarAccess !== false
    },
    accountHasError() {
      const account =
        this.calendarEventsMap?.[
          getCalendarAccountKey(this.account.email, this.account.calendarType)
        ]
      return account?.error && account?.calendarEvents?.length === 0
    },
    /** don't show account if in toggle state and account has an error */
    showAccount() {
      return !(this.toggleState && this.accountHasError)
    },
    reauthenticateBtnText() {
      if (this.account.calendarType == calendarTypes.GOOGLE) {
        return "Calendar access not granted, click to reauthenticate"
      } else if (this.account.calendarType == calendarTypes.APPLE) {
        return "Error with Apple Calendar account, click to remove"
      } else if (this.account.calendarType == calendarTypes.OUTLOOK) {
        return "Error with Outlook Calendar account, click to remove"
      }
    },
  },

  methods: {
    ...mapActions(["showError"]),
    addCalendarAccount() {
      signInGoogle({
        state: {
          type: this.toggleState
            ? authTypes.ADD_CALENDAR_ACCOUNT_FROM_EDIT
            : authTypes.ADD_CALENDAR_ACCOUNT,
          eventId: this.eventId,
        },
        requestCalendarPermission: true,
        selectAccount: true,
      })
    },
    reauthenticateCalendarAccount() {
      if (this.account.calendarType == calendarTypes.GOOGLE) {
        signInGoogle({
          state: {
            type: this.toggleState
              ? authTypes.ADD_CALENDAR_ACCOUNT_FROM_EDIT
              : authTypes.ADD_CALENDAR_ACCOUNT,
            eventId: this.eventId,
          },
          requestCalendarPermission: true,
          selectAccount: false,
          loginHint: this.account.email,
        })
      } else if (this.account.calendarType == calendarTypes.APPLE) {
        this.openRemoveDialog()
      } else if (this.account.calendarType == calendarTypes.OUTLOOK) {
        this.openRemoveDialog()
      }
    },
    toggleSubCalendarAccount(enabled, subCalendarId) {
      if (this.syncWithBackend) {
        post(`/user/toggle-sub-calendar`, {
          email: this.account.email,
          calendarType: this.account.calendarType,
          enabled,
          subCalendarId,
        }).catch((err) => {
          this.showError(
            "There was a problem with toggling your calendar account! Please try again later."
          )
        })
      } else {
        this.$emit("toggleSubCalendarAccount", {
          email: this.account.email,
          calendarType: this.account.calendarType,
          enabled,
          subCalendarId,
        })
      }
    },
    toggleCalendarAccount(enabled) {
      if (!enabled) this.showSubCalendars = false

      if (this.syncWithBackend) {
        post(`/user/toggle-calendar`, {
          email: this.account.email,
          calendarType: this.account.calendarType,
          enabled,
        }).catch((err) => {
          this.showError(
            "There was a problem with toggling your calendar account! Please try again later."
          )
        })
      } else {
        this.$emit("toggleCalendarAccount", {
          email: this.account.email,
          calendarType: this.account.calendarType,
          enabled,
        })
      }
    },
    openRemoveDialog() {
      this.$emit("openRemoveDialog", {
        email: this.account.email,
        calendarType: this.account.calendarType,
      })
    },

    // Handle auto disconnect change - FIXED: using imported patch function
    // Handle auto disconnect change
    async handleAutoDisconnectChange(newValue) {
      console.log("Auto disconnect change triggered:", newValue)
      console.log(
        "Account:",
        this.account.email,
        "Type:",
        this.account.calendarType
      )

      this.autoDisconnect = newValue

      if (this.syncWithBackend && !this.toggleState) {
        try {
          console.log("Making API call to update auto disconnect...")

          // Create payload with exact field names expected by Go backend
          const payload = {
            email: this.account.email,
            calendarType: this.account.calendarType,
            autoDisconnect: newValue,
          }

          console.log("Sending payload:", payload)

          const response = await patch(
            "/user/calendar-auto-disconnect",
            payload
          )

          console.log("Auto disconnect update successful:", response)
        } catch (error) {
          console.error("Failed to update auto disconnect setting:", error)

          // More detailed error logging
          if (error.response) {
            console.error("Error response status:", error.response.status)
            console.error("Error response data:", error.response.data)
            console.error("Error response headers:", error.response.headers)
          } else if (error.request) {
            console.error("No response received:", error.request)
          } else {
            console.error("Error setting up request:", error.message)
          }

          this.showError("Failed to update auto disconnect setting")
          // Revert on error
          this.autoDisconnect = !newValue
        }
      }
    },
  },

  // Initialize autoDisconnect from account data
  created() {
    // Initialize autoDisconnect from account data, default to true
    this.autoDisconnect =
      this.account.autoDisconnect !== undefined
        ? this.account.autoDisconnect
        : true
    console.log("CalendarAccount created:", {
      email: this.account.email,
      autoDisconnect: this.autoDisconnect,
      accountData: this.account,
    })
  },
}
</script>
